import type {
  BundleEntitlementKey,
  EntitlementKey,
  PermanentPurchaseId,
  RewardCodeId,
  TokenPackId,
} from "@/features/payment/types";

export interface RewardCodeDefinition {
  readonly id: RewardCodeId;
  readonly rewardToken: number;
}

export type PermanentPurchaseEffect =
  | { readonly kind: "mpCapacity"; readonly amount: number }
  | { readonly kind: "hovGainPercent"; readonly percent: number }
  | { readonly kind: "timGainPercent"; readonly percent: number };

export interface PermanentPurchaseDefinition {
  readonly id: PermanentPurchaseId;
  readonly baseCostToken: number;
  readonly costIncreasePerPurchase: number;
  readonly maxPurchases: number;
  readonly effect: PermanentPurchaseEffect;
}

export interface BundleDefinition {
  readonly id: BundleEntitlementKey;
  readonly sku: string;
  readonly price: number;
  readonly currency: string;
  readonly tokenGrant: number;
  readonly hovGrant: number;
  readonly timGrant: number;
  readonly permanentEffectGrants: readonly PermanentPurchaseId[];
  readonly entitlementKeys: readonly EntitlementKey[];
}

export interface TokenPackDefinition {
  readonly id: TokenPackId;
  readonly sku: string;
  readonly price: number;
  readonly currency: string;
  readonly tokenGrant: number;
}

export const REWARD_CODE_DEFINITIONS: readonly RewardCodeDefinition[] = [
  {
    id: "HoverIdleStart",
    rewardToken: 500,
  },
  {
    id: "AkiWeb8528",
    rewardToken: 500,
  },
];

export const PERMANENT_PURCHASE_DEFINITIONS: readonly PermanentPurchaseDefinition[] =
  [
    {
      id: "addMousePointer",
      baseCostToken: 300,
      costIncreasePerPurchase: 0,
      maxPurchases: 1,
      effect: { kind: "mpCapacity", amount: 1 },
    },
    {
      id: "hovGainPercent",
      baseCostToken: 1500,
      costIncreasePerPurchase: 500,
      maxPurchases: 5,
      effect: { kind: "hovGainPercent", percent: 10 },
    },
    {
      id: "timGainPercent",
      baseCostToken: 1500,
      costIncreasePerPurchase: 500,
      maxPurchases: 5,
      effect: { kind: "timGainPercent", percent: 5 },
    },
  ];

export const BUNDLE_DEFINITIONS: readonly BundleDefinition[] = [
  {
    id: "starter_bundle",
    sku: "starter_bundle",
    price: 600,
    currency: "JPY",
    tokenGrant: 500,
    hovGrant: 100,
    timGrant: 0,
    permanentEffectGrants: [],
    entitlementKeys: [],
  },
  {
    id: "tim_boost_bundle",
    sku: "tim_boost_bundle",
    price: 1000,
    currency: "JPY",
    tokenGrant: 1000,
    hovGrant: 0,
    timGrant: 100,
    permanentEffectGrants: ["timGainPercent"],
    entitlementKeys: [],
  },
];

export const TOKEN_PACK_DEFINITIONS: readonly TokenPackDefinition[] = [
  {
    id: "token_pack_100jpy",
    sku: "token_pack_100jpy",
    price: 100,
    currency: "JPY",
    tokenGrant: 1000,
  },
  {
    id: "token_pack_500jpy",
    sku: "token_pack_500jpy",
    price: 500,
    currency: "JPY",
    tokenGrant: 5500,
  },
  {
    id: "token_pack_2000jpy",
    sku: "token_pack_2000jpy",
    price: 2000,
    currency: "JPY",
    tokenGrant: 30000,
  },
  {
    id: "token_pack_5000jpy",
    sku: "token_pack_5000jpy",
    price: 5000,
    currency: "JPY",
    tokenGrant: 65000,
  },
];

const TOKEN_PACK_IDS = new Set<TokenPackId>(
  TOKEN_PACK_DEFINITIONS.map((definition) => definition.id),
);

const REWARD_CODE_IDS = new Set<RewardCodeId>(
  REWARD_CODE_DEFINITIONS.map((definition) => definition.id),
);
const BUNDLE_ENTITLEMENT_KEYS = new Set<BundleEntitlementKey>(
  BUNDLE_DEFINITIONS.map((definition) => definition.id),
);

export function isRewardCodeId(value: string): value is RewardCodeId {
  return REWARD_CODE_IDS.has(value as RewardCodeId);
}

export function isBundleEntitlementKey(
  value: string,
): value is BundleEntitlementKey {
  return BUNDLE_ENTITLEMENT_KEYS.has(value as BundleEntitlementKey);
}

export function getRewardCodeDefinition(
  id: RewardCodeId,
): RewardCodeDefinition {
  const definition = REWARD_CODE_DEFINITIONS.find((entry) => entry.id === id);
  if (!definition) {
    throw new Error(`Unknown reward code id: ${id}`);
  }
  return definition;
}

export function getPermanentPurchaseDefinition(
  id: PermanentPurchaseId,
): PermanentPurchaseDefinition {
  const definition = PERMANENT_PURCHASE_DEFINITIONS.find(
    (entry) => entry.id === id,
  );
  if (!definition) {
    throw new Error(`Unknown permanent purchase id: ${id}`);
  }
  return definition;
}

export function getBundleDefinition(
  id: BundleEntitlementKey,
): BundleDefinition {
  const definition = BUNDLE_DEFINITIONS.find((entry) => entry.id === id);
  if (!definition) {
    throw new Error(`Unknown bundle id: ${id}`);
  }
  return definition;
}

export function isTokenPackId(value: string): value is TokenPackId {
  return TOKEN_PACK_IDS.has(value as TokenPackId);
}

export function getTokenPackDefinition(id: TokenPackId): TokenPackDefinition {
  const definition = TOKEN_PACK_DEFINITIONS.find((entry) => entry.id === id);
  if (!definition) {
    throw new Error(`Unknown token pack id: ${id}`);
  }
  return definition;
}

export function getPermanentPurchaseNextCost(
  definition: PermanentPurchaseDefinition,
  currentCount: number,
): number {
  return (
    definition.baseCostToken + definition.costIncreasePerPurchase * currentCount
  );
}
